import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventsApi, teamsApi, gamesApi } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { CalendarIcon, TrophyIcon, SwordsIcon, DumbbellIcon, GraduationIcon, ClipboardIcon, PlusIcon, CloseIcon } from '../Icons/Icons';
import DateTimeInput from '../DateTimeInput/DateTimeInput';
import './EventsList.css';

const EventsList = () => {
  const navigate = useNavigate();
  const { user, canManageTeam } = useAuth();
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [filters, setFilters] = useState({
    team_id: '',
    game_id: '',
    search: ''
  });
  const [loading, setLoading] = useState(true);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [pastEventsLimit, setPastEventsLimit] = useState(10);
  const [dateRange, setDateRange] = useState({
    startMonth: '',
    endMonth: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newEvent, setNewEvent] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [eventsRes, teamsRes, gamesRes] = await Promise.all([
        eventsApi.getAll(),
        teamsApi.getAll(),
        gamesApi.getAll()
      ]);
      setEvents(eventsRes.data);
      setTeams(teamsRes.data);
      setGames(gamesRes.data);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEventTypeLabel = (type) => {
    const labels = {
      tournament: 'Tournoi',
      scrim: 'Scrim',
      training: 'Entraînement',
      coaching: 'Coaching',
      meeting: 'Réunion'
    };
    return labels[type] || type;
  };

  const getEventTypeIcon = (type, size = 14) => {
    const icons = {
      tournament: <TrophyIcon size={size} />,
      scrim: <SwordsIcon size={size} />,
      training: <DumbbellIcon size={size} />,
      coaching: <GraduationIcon size={size} />,
      meeting: <ClipboardIcon size={size} />
    };
    return icons[type] || <CalendarIcon size={size} />;
  };

  const getTeamNames = (event) => {
    // Gérer ancien format (team_id) et nouveau (team_ids)
    const teamIds = event.team_ids && event.team_ids.length > 0
      ? event.team_ids
      : (event.team_id ? [event.team_id] : []);

    if (teamIds.length === 0) {
      return 'Tous les jeux';
    }

    const teamNames = teamIds
      .map(id => teams.find(t => t.id === id)?.name)
      .filter(Boolean);
    return teamNames.join(', ') || 'Équipe inconnue';
  };

  const getGameForEvent = (event) => {
    // Gérer ancien format (team_id) et nouveau (team_ids)
    const teamIds = event.team_ids && event.team_ids.length > 0
      ? event.team_ids
      : (event.team_id ? [event.team_id] : []);

    if (teamIds.length === 0) return null;

    const firstTeam = teams.find(t => t.id === teamIds[0]);
    if (!firstTeam) return null;
    return games.find(g => g.id === firstTeam.game_id);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isEventPast = (event) => {
    return new Date(event.end_datetime) < new Date();
  };

  const filteredEvents = events.filter(event => {
    // Filtre par recherche
    if (filters.search && !event.title.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }

    // Filtre par équipe
    if (filters.team_id) {
      const teamId = parseInt(filters.team_id);
      // Gérer à la fois team_id (ancien) et team_ids (nouveau)
      const hasTeam = (event.team_ids && event.team_ids.includes(teamId)) || event.team_id === teamId;
      if (!hasTeam) {
        return false;
      }
    }

    // Filtre par jeu
    if (filters.game_id) {
      const gameId = parseInt(filters.game_id);
      const eventGame = getGameForEvent(event);
      if (!eventGame || eventGame.id !== gameId) {
        return false;
      }
    }

    return true;
  });

  // Séparer les événements futurs et passés
  const upcomingEvents = filteredEvents.filter(e => !isEventPast(e)).sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
  let pastEvents = filteredEvents.filter(e => isEventPast(e)).sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime));

  // Filtrer par plage de dates si définie
  if (dateRange.startMonth || dateRange.endMonth) {
    pastEvents = pastEvents.filter(event => {
      const eventDate = new Date(event.start_datetime);
      const eventMonthKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;

      if (dateRange.startMonth && dateRange.endMonth) {
        return eventMonthKey >= dateRange.startMonth && eventMonthKey <= dateRange.endMonth;
      } else if (dateRange.startMonth) {
        return eventMonthKey >= dateRange.startMonth;
      } else if (dateRange.endMonth) {
        return eventMonthKey <= dateRange.endMonth;
      }
      return true;
    });
  }

  // Grouper les événements passés par mois
  const groupEventsByMonth = (events) => {
    const grouped = {};
    events.forEach(event => {
      const date = new Date(event.start_datetime);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          label: monthLabel,
          events: []
        };
      }
      grouped[monthKey].events.push(event);
    });
    return grouped;
  };

  const groupedPastEvents = groupEventsByMonth(pastEvents);
  const displayedPastEvents = showPastEvents ? pastEvents : pastEvents.slice(0, pastEventsLimit);

  // Générer la liste des mois disponibles pour les événements passés
  const allPastEvents = filteredEvents.filter(e => isEventPast(e));
  const availableMonths = [...new Set(allPastEvents.map(event => {
    const date = new Date(event.start_datetime);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }))].sort().reverse();

  const getMonthLabel = (monthKey) => {
    if (!monthKey) return '';
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const handleCreateEvent = () => {
    const now = new Date();
    const startTime = new Date(now.getTime() + 60 * 60 * 1000); // +1h
    const endTime = new Date(startTime.getTime() + 2 * 60 * 60 * 1000); // +2h

    setNewEvent({
      title: '',
      type: 'training',
      team_id: null,
      game_id: null,
      team_ids: [],
      scope: 'all',
      is_online: 1,
      location: '',
      start_datetime: startTime.toISOString().slice(0, 16),
      end_datetime: endTime.toISOString().slice(0, 16),
      notes: ''
    });
    setShowCreateModal(true);
  };

  const handleSubmitNewEvent = async (e) => {
    e.preventDefault();
    try {
      const eventData = { ...newEvent };

      if (newEvent.scope === 'all') {
        eventData.team_ids = teams.map(t => t.id);
        eventData.team_id = teams[0]?.id || null;
      } else if (newEvent.scope === 'game') {
        const gameTeams = teams.filter(t => t.game_id === newEvent.game_id);
        eventData.team_ids = gameTeams.map(t => t.id);
        eventData.team_id = gameTeams[0]?.id || null;
      } else {
        eventData.team_ids = [newEvent.team_id];
      }

      delete eventData.scope;
      delete eventData.game_id;

      await eventsApi.create(eventData);
      setShowCreateModal(false);
      setNewEvent(null);
      loadData();
    } catch (error) {
      console.error('Erreur création événement:', error);
      alert('Erreur lors de la création de l\'événement');
    }
  };

  return (
    <div className="events-list-page">
      <div className="events-header">
        <div>
          <h1>Événements</h1>
          <p className="events-subtitle">Gérez et consultez tous les événements de vos équipes</p>
        </div>
        {canManageTeam() && (
          <button
            className="btn-create-global"
            onClick={handleCreateEvent}
            title="Créer un nouvel événement"
          >
            <PlusIcon size={18} /> Nouvel événement
          </button>
        )}
      </div>

      {/* Filtres */}
      <div className="events-filters">
        <input
          type="text"
          placeholder="Rechercher un événement..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="search-input"
        />
        <select
          value={filters.game_id}
          onChange={(e) => setFilters({ ...filters, game_id: e.target.value })}
          className="filter-select"
        >
          <option value="">Tous les jeux</option>
          {games.map(game => (
            <option key={game.id} value={game.id}>{game.name}</option>
          ))}
        </select>
        <select
          value={filters.team_id}
          onChange={(e) => setFilters({ ...filters, team_id: e.target.value })}
          className="filter-select"
        >
          <option value="">Toutes les équipes</option>
          {teams.map(team => (
            <option key={team.id} value={team.id}>{team.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading-state">Chargement...</div>
      ) : (
        <>
          {/* Événements à venir */}
          {upcomingEvents.length > 0 && (
            <div className="events-section">
              <h2 className="section-title">
                À venir ({upcomingEvents.length})
              </h2>
              <div className="events-grid">
                {upcomingEvents.map(event => (
                  <div
                    key={event.id}
                    className="event-card"
                    onClick={() => navigate(`/events/${event.id}`)}
                  >
                    <div className="event-card-header">
                      <span className="event-type-badge" data-type={event.type}>
                        {getEventTypeIcon(event.type)} {getEventTypeLabel(event.type)}
                      </span>
                      <span className="event-status">À venir</span>
                    </div>
                    <h3 className="event-title">{event.title}</h3>
                    <div className="event-meta">
                      <div className="event-meta-item">
                        <CalendarIcon size={16} />
                        <span>{formatDate(event.start_datetime)}</span>
                      </div>
                      <div className="event-meta-item">
                        <span className="event-time">
                          {formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}
                        </span>
                      </div>
                    </div>
                    <div className="event-teams">
                      {getTeamNames(event)}
                    </div>
                    <div className="event-format">
                      {event.is_online ? '🌐 En ligne' : '📍 Présentiel'}
                      {!event.is_online && event.location && (
                        <span className="event-location">
                          {' — '}
                          {event.location.startsWith('http') ? (
                            <a href={event.location} target="_blank" rel="noopener noreferrer">Voir le lieu</a>
                          ) : event.location}
                        </span>
                      )}
                    </div>
                    {event.description && (
                      <p className="event-description">{event.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Événements passés */}
          {allPastEvents.length > 0 && (
            <div className="events-section past-section">
              <div className="past-section-header">
                <h2 className="section-title">
                  Événements passés ({pastEvents.length})
                </h2>
                <div className="past-section-controls">
                  {/* Filtres de période */}
                  {availableMonths.length > 1 && (
                    <div className="date-range-filters">
                      <select
                        value={dateRange.startMonth}
                        onChange={(e) => setDateRange({ ...dateRange, startMonth: e.target.value })}
                        className="filter-select month-select"
                      >
                        <option value="">Début</option>
                        {availableMonths.map(month => (
                          <option key={month} value={month}>{getMonthLabel(month)}</option>
                        ))}
                      </select>
                      <span className="date-range-separator">→</span>
                      <select
                        value={dateRange.endMonth}
                        onChange={(e) => setDateRange({ ...dateRange, endMonth: e.target.value })}
                        className="filter-select month-select"
                      >
                        <option value="">Fin</option>
                        {availableMonths.map(month => (
                          <option key={month} value={month}>{getMonthLabel(month)}</option>
                        ))}
                      </select>
                      {(dateRange.startMonth || dateRange.endMonth) && (
                        <button
                          className="btn-clear-range"
                          onClick={() => setDateRange({ startMonth: '', endMonth: '' })}
                          title="Réinitialiser"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  )}
                  {pastEvents.length > pastEventsLimit && (
                    <button
                      className="btn-toggle-past"
                      onClick={() => setShowPastEvents(!showPastEvents)}
                    >
                      {showPastEvents ? '📋 Compact' : '📅 Tout afficher'}
                    </button>
                  )}
                </div>
              </div>

              {showPastEvents ? (
                // Affichage groupé par mois
                <div className="past-events-grouped">
                  {Object.entries(groupedPastEvents).map(([monthKey, { label, events }]) => (
                    <div key={monthKey} className="month-group">
                      <h3 className="month-label">{label}</h3>
                      <div className="past-events-list">
                        {events.map(event => (
                          <div
                            key={event.id}
                            className="past-event-item"
                            onClick={() => navigate(`/events/${event.id}`)}
                          >
                            <div className="past-event-date">
                              <span className="past-event-day">
                                {new Date(event.start_datetime).getDate()}
                              </span>
                            </div>
                            <div className="past-event-content">
                              <div className="past-event-title-row">
                                <span className="past-event-type-icon" data-type={event.type}>
                                  {getEventTypeIcon(event.type, 16)}
                                </span>
                                <h4>{event.title}</h4>
                              </div>
                              <div className="past-event-meta">
                                <span className="past-event-time">
                                  {formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}
                                </span>
                                <span className="past-event-separator">•</span>
                                <span className="past-event-teams">{getTeamNames(event)}</span>
                              </div>
                            </div>
                            <span className="past-event-badge" data-type={event.type}>
                              {getEventTypeLabel(event.type)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Affichage compact (liste simple)
                <div className="past-events-compact">
                  {displayedPastEvents.map(event => (
                    <div
                      key={event.id}
                      className="past-event-item"
                      onClick={() => navigate(`/events/${event.id}`)}
                    >
                      <div className="past-event-date">
                        <span className="past-event-day">
                          {new Date(event.start_datetime).getDate()}
                        </span>
                        <span className="past-event-month">
                          {new Date(event.start_datetime).toLocaleDateString('fr-FR', { month: 'short' })}
                        </span>
                      </div>
                      <div className="past-event-content">
                        <div className="past-event-title-row">
                          <span className="past-event-type-icon" data-type={event.type}>
                            {getEventTypeIcon(event.type, 16)}
                          </span>
                          <h4>{event.title}</h4>
                        </div>
                        <div className="past-event-meta">
                          <span className="past-event-time">
                            {formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}
                          </span>
                          <span className="past-event-separator">•</span>
                          <span className="past-event-teams">{getTeamNames(event)}</span>
                        </div>
                      </div>
                      <span className="past-event-badge" data-type={event.type}>
                        {getEventTypeLabel(event.type)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {filteredEvents.length === 0 && (
            <div className="empty-state">
              <CalendarIcon size={48} />
              <p>Aucun événement trouvé</p>
            </div>
          )}
        </>
      )}

      {/* Modale de création d'événement */}
      {showCreateModal && newEvent && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setShowCreateModal(false)}
              type="button"
              aria-label="Fermer"
            >
              ✕
            </button>
            <h2>Créer un événement</h2>
            <form onSubmit={handleSubmitNewEvent}>
              <div className="form-group">
                <label>Titre</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  placeholder="Nom de l'événement"
                  required
                />
              </div>

              <div className="form-group">
                <label>Type</label>
                <select
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({...newEvent, type: e.target.value})}
                  required
                >
                  <option value="tournament">Tournoi</option>
                  <option value="scrim">Scrim</option>
                  <option value="training">Entraînement</option>
                  <option value="coaching">Coaching</option>
                  <option value="meeting">Réunion</option>
                </select>
              </div>

              <div className="form-group">
                <label>Portée de l'événement</label>
                <select
                  value={newEvent.scope}
                  onChange={(e) => setNewEvent({...newEvent, scope: e.target.value})}
                  required
                >
                  <option value="single">Une seule équipe</option>
                  <option value="game">Toutes les équipes d'un jeu</option>
                  <option value="all">Toutes les équipes</option>
                </select>
              </div>

              {newEvent.scope === 'single' && (
                <div className="form-group">
                  <label>Équipe</label>
                  <select
                    value={newEvent.team_id || ''}
                    onChange={(e) => setNewEvent({...newEvent, team_id: parseInt(e.target.value)})}
                    required
                  >
                    <option value="">Sélectionner une équipe</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {newEvent.scope === 'game' && (
                <div className="form-group">
                  <label>Jeu</label>
                  <select
                    value={newEvent.game_id || ''}
                    onChange={(e) => setNewEvent({...newEvent, game_id: parseInt(e.target.value)})}
                    required
                  >
                    <option value="">Sélectionner un jeu</option>
                    {games.map(game => (
                      <option key={game.id} value={game.id}>{game.name}</option>
                    ))}
                  </select>
                  <small style={{color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block'}}>
                    {newEvent.game_id && `${teams.filter(t => t.game_id === newEvent.game_id).length} équipe(s) concernée(s)`}
                  </small>
                </div>
              )}

              {newEvent.scope === 'all' && (
                <div className="form-group">
                  <small style={{color: 'var(--text-muted)', fontSize: '0.85rem'}}>
                    Cet événement sera visible pour toutes les équipes.
                  </small>
                </div>
              )}

              <div className="form-group">
                <label>Format</label>
                <select
                  value={newEvent.is_online ? 'online' : 'offline'}
                  onChange={(e) => setNewEvent({...newEvent, is_online: e.target.value === 'online' ? 1 : 0, location: e.target.value === 'online' ? '' : newEvent.location})}
                >
                  <option value="online">En ligne</option>
                  <option value="offline">Présentiel</option>
                </select>
              </div>

              {!newEvent.is_online && (
                <div className="form-group">
                  <label>Lieu</label>
                  <input
                    type="text"
                    value={newEvent.location || ''}
                    onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                    placeholder="Adresse ou lien Google Maps / Mappy..."
                  />
                </div>
              )}

              <div className="form-row">
                <DateTimeInput
                  label="Début"
                  value={newEvent.start_datetime}
                  onChange={(val) => setNewEvent({...newEvent, start_datetime: val})}
                  required
                />
                <DateTimeInput
                  label="Fin"
                  value={newEvent.end_datetime}
                  onChange={(val) => setNewEvent({...newEvent, end_datetime: val})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newEvent.notes || ''}
                  onChange={(e) => setNewEvent({...newEvent, notes: e.target.value})}
                  placeholder="Informations complémentaires..."
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowCreateModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Créer l'événement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsList;
